import { useRef, useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Divider,
  FileButton,
  Group,
  Modal,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import {
  usePortalOrderControllerPreview,
  usePortalOrderControllerCommit,
  usePortalOrderControllerBauzeitenplan,
  type BauzeitenplanVorschauDtoOutput,
  type ApiError,
  type ParsedPortalOrderDtoOutput,
  type PortalOrderCommitDto,
} from '@werkbank/shared';
import { EuroInput } from './EuroInput';

/** Datei-Bytes → base64 (das PDF wird serverseitig via pdftotext gelesen). */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

/**
 * Portal-Auftrag per PDF-Upload (docs/spec/portal-pdf-import.md): Datei
 * wählen/ziehen → Server parst (schreibt nichts) → erkannte Felder
 * prüfen/korrigieren → „Auftrag anlegen" (legt Objekt/Wohnung dedupliziert an,
 * dann den Auftrag). Unterstützt die Vorlagen der GSW (Handwerkerportal) und der
 * Wohnbau Mainz (Immo-Office); die Vorlage wird am Inhalt erkannt und bestimmt
 * den Kunden. Idempotent über die Auftragsnummer.
 */
export function PortalImportModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [filename, setFilename] = useState('');
  const [parsed, setParsed] = useState<ParsedPortalOrderDtoOutput | null>(null);
  // Das hochgeladene Original bleibt hier liegen und reist beim Anlegen mit —
  // der Auftrag bewahrt es auf, damit es später ausgefüllt ins Portal
  // zurückgeladen werden kann (docs/spec/portal-pdf-import.md).
  const [contentBase64, setContentBase64] = useState<string | null>(null);
  // Bischof wird unter zwei Kundennummern geführt (10314/10346) und das
  // Dokument unterscheidet sie nicht — deshalb wählt das Büro hier. Bewusst
  // OHNE Vorbelegung: Eine geratene Vorauswahl würde stillschweigend die
  // falsche Firma belasten (Faruq 2026-08-29).
  const [customerId, setCustomerId] = useState<string | null>(null);
  // Tobias 2026-10-07: Yarowa-Aufträge sind GWH-Aufträge ODER
  // Mieterdirektbeauftragungen — Pflichtwahl beim Hochladen, ohne Vorbelegung.
  const [beauftragung, setBeauftragung] = useState<'GWH_BEAUFTRAGUNG' | 'MIETERDIREKT' | null>(
    null,
  );
  // Faruq 2026-09-04: optionaler Bauzeitenplan (nur Wohnbau Mainz) - unsere
  // Schreiner-Zeile wird gelesen, das spaeteste Datum daraus wird Faelligkeit.
  const [plan, setPlan] = useState<{ base64: string; name: string } | null>(null);
  const [planVorschau, setPlanVorschau] = useState<BauzeitenplanVorschauDtoOutput | null>(null);
  const [planFaellig, setPlanFaellig] = useState('');
  const planLesen = usePortalOrderControllerBauzeitenplan<ApiError>({
    mutation: {
      onSuccess: (r) => {
        setPlanVorschau(r);
        setPlanFaellig(r.faelligAm ?? '');
      },
    },
  });
  const planWaehlen = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const b64 = result.includes(',') ? result.slice(result.indexOf(',') + 1) : result;
      setPlan({ base64: b64, name: file.name });
      setPlanVorschau(null);
      planLesen.mutate({ data: { contentBase64: b64, filename: file.name } });
    };
    reader.readAsDataURL(file);
  };
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const preview = usePortalOrderControllerPreview<ApiError>({
    mutation: { onSuccess: (r) => setParsed(r) },
  });
  const commit = usePortalOrderControllerCommit<ApiError>({
    mutation: {
      onSuccess: (r) => {
        reset();
        onClose();
        void navigate({ to: '/orders/$orderId', params: { orderId: r.orderId } });
      },
    },
  });

  const reset = () => {
    setParsed(null);
    setPlan(null);
    setPlanVorschau(null);
    setPlanFaellig('');
    setCustomerId(null);
    setBeauftragung(null);
    setContentBase64(null);
    setFilename('');
    setDragging(false);
    preview.reset();
    commit.reset();
    if (inputRef.current) inputRef.current.value = '';
  };
  const close = () => {
    reset();
    onClose();
  };

  const onFile = async (file: File | null) => {
    if (!file) return;
    setFilename(file.name);
    setParsed(null);
    commit.reset();
    const b64 = await fileToBase64(file);
    setContentBase64(b64);
    preview.mutate({ data: { contentBase64: b64, filename: file.name } });
  };

  const patch = (p: Partial<ParsedPortalOrderDtoOutput>) =>
    setParsed((cur) => (cur ? { ...cur, ...p } : cur));
  const patchAo = (p: { street?: string; zip?: string; city?: string }) =>
    setParsed((cur) =>
      cur && cur.ausfuehrungsort
        ? { ...cur, ausfuehrungsort: { ...cur.ausfuehrungsort, ...p } }
        : cur,
    );
  const patchMieter = (p: { name?: string; telefon?: string; email?: string }) =>
    setParsed((cur) => (cur && cur.mieter ? { ...cur, mieter: { ...cur.mieter, ...p } } : cur));

  return (
    <Modal opened={opened} onClose={close} title="Auftrag importieren" size="lg">
      <Stack gap="md">
        {!parsed && (
          <>
            <div className="wb-info-banner">
              Laden Sie das Auftrags-PDF der <strong>GSW</strong>, der{' '}
              <strong>Wohnbau Mainz</strong>, von <strong>Bischof</strong> oder aus{' '}
              <strong>Yarowa</strong> (GWH) hoch — die Vorlage wird automatisch erkannt. Objekt,
              Mieter und Leistungstext werden ausgelesen; Sie prüfen sie und legen daraus den
              Auftrag an. Bis „Auftrag anlegen" wird nichts gespeichert.
            </div>

            <div
              className="wb-upload-drop"
              data-drag={dragging ? 'true' : undefined}
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                void onFile(e.dataTransfer.files?.[0] ?? null);
              }}
              data-testid="portal-drop"
            >
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,application/pdf"
                hidden
                onChange={(e) => void onFile(e.currentTarget.files?.[0] ?? null)}
              />
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M12 16V4" />
                <path d="m7 9 5-5 5 5" />
                <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
              </svg>
              <Text fw={600} size="sm">
                {preview.isPending ? 'PDF wird gelesen…' : 'PDF hierher ziehen'}
              </Text>
              <Text size="xs" c="dimmed">
                {filename || 'oder klicken, um eine Datei zu wählen'}
              </Text>
            </div>

            {preview.error && (
              <Alert color="red" data-testid="portal-preview-error">
                {preview.error.message}
              </Alert>
            )}
          </>
        )}

        {parsed && (
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text size="sm" c="dimmed">
                {parsed.portal === 'WOHNBAU'
                  ? 'Wohnbau Mainz'
                  : parsed.portal === 'BISCHOF'
                    ? 'Bischof Umweltservice'
                    : parsed.portal === 'YAROWA'
                      ? 'Yarowa (GWH)'
                      : 'GSW'}{' '}
                · <strong>{parsed.bestellnummer}</strong>
                {filename ? ` (${filename})` : ''}
              </Text>
              <Anchor size="sm" onClick={reset} data-testid="portal-other-file">
                Andere Datei
              </Anchor>
            </Group>

            {/* Yarowa (Faruq 2026-09-05): der Rechnungsempfänger des Auftragszettels
                bestimmt den Kunden — sichtbar machen, damit eine Auswahl unten
                nachvollziehbar ist. */}
            {parsed.portal === 'YAROWA' && (
              <Text size="sm" data-testid="portal-rechnungsempfaenger">
                Rechnungsempfänger laut Auftragszettel:{' '}
                <strong>{parsed.rechnungsempfaengerName ?? '(nicht erkannt)'}</strong>
              </Text>
            )}
            {parsed.portal === 'YAROWA' && (
              <Radio.Group
                label="Beauftragungsart"
                description="Pflicht: Steht auf dem Auftrag als Badge und steuert Rechnung und Hausmeister-Nachweis."
                withAsterisk
                value={beauftragung ?? ''}
                onChange={(v) => setBeauftragung(v as 'GWH_BEAUFTRAGUNG' | 'MIETERDIREKT')}
                data-testid="portal-beauftragung"
              >
                <Group mt={6} gap="lg">
                  <Radio
                    value="GWH_BEAUFTRAGUNG"
                    label="GWH-Auftrag"
                    data-testid="portal-beauftragung-gwh"
                  />
                  <Radio
                    value="MIETERDIREKT"
                    label="Mieterdirektbeauftragung"
                    data-testid="portal-beauftragung-mieterdirekt"
                  />
                </Group>
              </Radio.Group>
            )}
            {parsed.kundenauswahl.length > 1 && (
              <Select
                label="Kunde"
                description={
                  parsed.portal === 'YAROWA'
                    ? 'Der Rechnungsempfänger passt zu mehreren Kundennummern — bitte die zutreffende wählen.'
                    : 'Für dieses Portal sind mehrere Kundennummern hinterlegt — bitte die zutreffende wählen.'
                }
                placeholder="Kundennummer wählen"
                // Die Pflichtreferenz gehört ins Label: Bei Bischof heißen
                // beide Kunden gleich und unterscheiden sich NUR darin
                // („Instandhaltung" vs. „Kostenstelle 0802"). Ohne sie waehlt
                // Tobias zwischen zwei identischen Zeilen.
                data={parsed.kundenauswahl.map((k) => ({
                  value: k.id,
                  label: k.refs.length
                    ? `${k.nummer} · ${k.name} · ${k.refs.join(', ')}`
                    : `${k.nummer} · ${k.name}`,
                }))}
                value={customerId}
                onChange={setCustomerId}
                data-testid="portal-customer"
              />
            )}

            <Group grow align="flex-start">
              <TextInput
                label="Bestellnummer"
                value={parsed.bestellnummer ?? ''}
                onChange={(e) => patch({ bestellnummer: e.currentTarget.value })}
                data-testid="portal-bestellnummer"
              />
              <EuroInput
                label="Vereinbart (netto)"
                value={parsed.agreedTotalCents != null ? parsed.agreedTotalCents / 100 : ''}
                onChange={(v) =>
                  patch({ agreedTotalCents: typeof v === 'number' ? Math.round(v * 100) : null })
                }
              />
            </Group>
            <TextInput
              label="Titel"
              value={parsed.title ?? ''}
              onChange={(e) => patch({ title: e.currentTarget.value })}
            />
            <Textarea
              label="Leistungstext"
              autosize
              minRows={2}
              maxRows={5}
              value={parsed.description ?? ''}
              onChange={(e) => patch({ description: e.currentTarget.value })}
            />

            {parsed.ausfuehrungsort && (
              <>
                <Divider label="Ausführungsort" labelPosition="left" />
                <TextInput
                  label="Straße"
                  value={parsed.ausfuehrungsort.street ?? ''}
                  onChange={(e) => patchAo({ street: e.currentTarget.value })}
                />
                <Group grow>
                  <TextInput
                    label="PLZ"
                    value={parsed.ausfuehrungsort.zip ?? ''}
                    onChange={(e) => patchAo({ zip: e.currentTarget.value })}
                  />
                  <TextInput
                    label="Ort"
                    value={parsed.ausfuehrungsort.city ?? ''}
                    onChange={(e) => patchAo({ city: e.currentTarget.value })}
                  />
                </Group>
                <Group grow>
                  <TextInput
                    label="Etage"
                    value={parsed.etage ?? ''}
                    onChange={(e) => patch({ etage: e.currentTarget.value })}
                  />
                  <TextInput
                    label="Lage"
                    value={parsed.lage ?? ''}
                    onChange={(e) => patch({ lage: e.currentTarget.value })}
                  />
                  <TextInput
                    label="Mieteinheit"
                    value={parsed.mieteinheit ?? ''}
                    onChange={(e) => patch({ mieteinheit: e.currentTarget.value })}
                  />
                </Group>
              </>
            )}

            {parsed.mieter && (
              <>
                <Divider label="Mieter" labelPosition="left" />
                <TextInput
                  label="Name"
                  value={parsed.mieter.name ?? ''}
                  onChange={(e) => patchMieter({ name: e.currentTarget.value })}
                  data-testid="portal-mieter-name"
                />
                <Group grow>
                  <TextInput
                    label="Telefon"
                    value={parsed.mieter.telefon ?? ''}
                    onChange={(e) => patchMieter({ telefon: e.currentTarget.value })}
                  />
                  <TextInput
                    label="E-Mail"
                    value={parsed.mieter.email ?? ''}
                    onChange={(e) => patchMieter({ email: e.currentTarget.value })}
                  />
                </Group>
              </>
            )}

            {commit.error && (
              <Alert color="red" data-testid="portal-commit-error">
                {commit.error.message}
              </Alert>
            )}
            {parsed.portal === 'WOHNBAU' && (
              <Stack gap={4} data-testid="portal-bauzeitenplan">
                <Text size="sm" fw={500}>
                  Bauzeitenplan (optional)
                </Text>
                <Text size="xs" c="dimmed">
                  Liegt zum Auftrag ein Bauzeitenplan vor, wird unsere Schreiner-Zeile gelesen; das
                  späteste Datum daraus wird als Fälligkeit hinterlegt, der Plan bleibt als Anhang
                  am Auftrag.
                </Text>
                <FileButton onChange={planWaehlen} accept="application/pdf">
                  {(props) => (
                    <Button
                      {...props}
                      size="xs"
                      variant="default"
                      loading={planLesen.isPending}
                      data-testid="portal-bauzeitenplan-upload"
                    >
                      {plan ? plan.name : 'Bauzeitenplan-PDF wählen'}
                    </Button>
                  )}
                </FileButton>
                {planVorschau && !planVorschau.gefunden && (
                  <Text size="sm" c="orange">
                    Keine Schreiner-/Schroedter-Zeile gefunden – Fälligkeit bitte von Hand setzen.
                  </Text>
                )}
                {planVorschau?.zeile && (
                  <Text size="sm" data-testid="portal-bauzeitenplan-zeile">
                    {planVorschau.zeile}
                  </Text>
                )}
                {planVorschau && (
                  <TextInput
                    type="date"
                    label="Fälligkeit aus dem Bauzeitenplan"
                    value={planFaellig}
                    onChange={(e) => setPlanFaellig(e.currentTarget.value)}
                    w={220}
                    data-testid="portal-bauzeitenplan-faellig"
                  />
                )}
              </Stack>
            )}
            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={close}>
                Abbrechen
              </Button>
              <Button
                loading={commit.isPending}
                // Ohne Kundenwahl kein Anlegen — sonst entscheidet der Zufall,
                // welche der beiden Firmen die Rechnung bekommt.
                disabled={
                  (parsed.kundenauswahl.length > 1 && !customerId) ||
                  (parsed.portal === 'YAROWA' && !beauftragung)
                }
                onClick={() =>
                  commit.mutate({
                    data: {
                      ...parsed,
                      contentBase64,
                      customerId,
                      ...(beauftragung ? { commissionType: beauftragung } : {}),
                      ...(plan
                        ? {
                            bauzeitenplanBase64: plan.base64,
                            bauzeitenplanFaelligAm: planFaellig || null,
                            bauzeitenplanZeile: planVorschau?.zeile ?? null,
                          }
                        : {}),
                    } as PortalOrderCommitDto,
                  })
                }
                data-testid="portal-commit"
              >
                Auftrag anlegen
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
